import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCurrentUserQuery, useLogoutMutation } from 'api';
import { notification } from 'components';
import { ADMIN_ROUTES } from 'constants/routes';
import { ErrorMappingValueType } from 'typings';

export const useAdminSideBar = () => {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [actionMenuOpen, setActionMenuOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [notificationApi, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );
  const userId = JSON.parse(localStorage.getItem('userId') || 'null');
  const { data = {} } = useCurrentUserQuery({ userId }, { skip: !userId });
  const [logout] = useLogoutMutation();

  const handleCancel = useCallback(() => setIsModalOpen(false), []);
  const toggleSidebarCollapse = useCallback(() => setCollapsed((prevState) => !prevState), []);
  const toggleActionMenu = useCallback(() => setActionMenuOpen((prevState) => !prevState), []);
  const handleActionMenu = useCallback(
    (action: string) => {
      switch (action) {
        case 'logout':
          setIsModalOpen(true);
          break;
        case 'profile':
          navigate(ADMIN_ROUTES.adminProfile);
          break;
        default:
      }
      setActionMenuOpen(false);
    },
    [navigate]
  );
  const handleOk = useCallback(() => {
    logout()
      .then(({ data }: any) => {
        if (data?.success) {
          localStorage.clear();
          navigate(ADMIN_ROUTES.adminSignIn);
        } else {
          openNotificationWithIcon({
            type: 'error',
            message: 'Something went wrong!',
          });
        }
      })
      .catch((err) => {
        console.log(err, 'error');
      })
      .finally(() => {
        setIsModalOpen(false);
      });
  }, [openNotificationWithIcon, logout, navigate]);

  useEffect(() => {
    const isOverflowHidden = document.body.style.overflow === 'hidden';
    if ((actionMenuOpen || !collapsed) && !isOverflowHidden) {
      document.body.style.overflow = 'hidden';
    } else if (!actionMenuOpen && collapsed && isOverflowHidden) {
      document.body.style.overflow = '';
    }
  }, [actionMenuOpen, collapsed]);

  const navigateHome = useCallback(() => {
    navigate(`${ADMIN_ROUTES.dashboardPage}/${ADMIN_ROUTES.dashboardList}`);
  }, [navigate]);

  return {
    pathname: location.pathname,
    collapsed,
    toggleSidebarCollapse,
    toggleActionMenu,
    actionMenuOpen,
    isModalOpen,
    handleCancel,
    handleOk,
    handleActionMenu,
    contextHolder,
    navigateHome,
    data,
  };
};
